<script setup>
import { computed, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Basic dialog wrapper with fixed size constraints; never goes full-screen on mobile.
 *
 * @status internal
 * @group cards-dialogs
 * @see https://quasar.dev/vue-components/dialog Quasar q-dialog API
 */
defineOptions({ name: 'HrBasicDialog' });

const props = defineProps({
  /**
   * Max-width tier. The dialog never goes full-screen, even on mobile.
   * @values sm, md
   */
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md'].includes(v),
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   */
  persistent: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props for backwards compatibility with the legacy theme.
   * @deprecated Only for backwards compatibility — remove once legacy consumers are migrated.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const dialogProps = computed(() => {
  const base = {
    ...attrs,
    persistent: props.persistent,
  };
  return hasTheme.value ? base : { ...props.legacyProps, ...base };
});

const qDialogRef = ref(null);

defineExpose({
  show: () => qDialogRef.value?.show(),
  hide: () => qDialogRef.value?.hide(),
});
</script>

<template>
  <q-dialog
    ref="qDialogRef"
    v-bind="dialogProps"
  >
    <div :class="['hr-basic-dialog', `hr-basic-dialog--${size}`]">
      <!-- @slot default Dialog content — typically a card with sections and actions. -->
      <slot />
    </div>
  </q-dialog>
</template>

<style lang="scss" scoped>
.hr-basic-dialog {
  width: 100%;
}

.hr-basic-dialog--sm {
  max-width: 360px;
  max-height: 90vh;
}

.hr-basic-dialog--md {
  max-width: 480px;
  max-height: 90vh;
}
</style>
